import React, { useState, useEffect, useRef } from "react";
import {
  // Navigation & Layout
  House,
  ChevronDown,
  Briefcase,

  // Master Module Icons
  Layers,
  Shield,
  UserCog,
  Building2,
  Briefcase as Department,
  Palette,
  FolderOpen,
  SquareMousePointer,
  Paintbrush,
  Clock,

  // Leads Module Icons
  FileQuestion,
  PhoneCall,
  MapPin,
  // FileText as Quotation,
  FileText,
  Clock as ClockIcon,
  Calculator as EstimateIcon,
  Ruler as SiteDesignerIcon,

  // HR Module Icons
  Users,
  UserCheck,
  CalendarDays,
  CalendarOff,
  Wallet,
  Leaf,
  CreditCard,
  IdCard,

  // Task Management
  ListTodo,

  // Clients
  Users2,

  // Projects
  FolderKanban,

  // Carpenter Module
  Hammer,
  Users as CarpenterIcon,
  Wrench,
  CheckCircle,

  // Inventory Module
  Boxes,
  Package,
  ShoppingBag,
  Archive,
  BarChart3,
  AlertTriangle,

  // Warehouse
  Warehouse,

  // Transport Module
  Truck,
  TruckIcon,
  UserCircle,
  MapPinned,
  PackageCheck,
  History,
  Radio,

  // Billing Module
  IndianRupee,
  Receipt,
  Plus,
  ClockArrowUp,
  FileSpreadsheet,
  RotateCcw,
  WalletCards,

  // Distributor & Trader Module Icons
  Store,
  Target,
  UsersRound,

  // Other Modules
  Megaphone,
  Video,
  Landmark,
  Activity,

  // Reports Module Icons
  LayoutDashboard as ReportsOverviewIcon,
  TrendingUp,
  ShoppingBag as PurchaseIcon,
  Package as StockIcon,
  FolderKanban as ProjectIcon,
  Users as LeadIcon,
  Store as DealerIcon,
  UserCheck as EmployeeIcon,
  Hammer as CarpenterReportIcon,
  Receipt as ExpenseIcon,
  AlertTriangle as DamageIcon,
  Truck as ShipmentIcon,
  Wallet as SalaryIcon,
  BarChart3 as BalanceSheetIcon,
  FileSpreadsheet as SalesLedgerIcon,
  CalendarDays as AttendanceReportIcon,
} from "lucide-react";

import { RiAdvertisementLine } from "react-icons/ri";
import "../Sidebar/Sidebar.css";
import Logo from "../../assests/image/Harwin_logo.png";
import { useNavigate, useLocation } from "react-router-dom";
import { usePageTitle } from "../../context/PageTitleContext";
import { usePermissions } from "../../context/PermissionContext";

// ─────────────────────────────────────────────
// Helper Functions
// ─────────────────────────────────────────────

const RIcon = ({ icon: Icon, className = "", size = 16 }) => (
  <span
    className={`submenu-icon ${className}`}
    style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: size,
      height: size,
      flexShrink: 0,
    }}
  >
    <Icon size={size} />
  </span>
);

const isReactIcon = (IconComponent) => {
  if (!IconComponent) return false;

  const name = IconComponent.displayName || IconComponent.name || "";

  return [
    "Ri",
    "Pi",
    "Fa",
    "Md",
    "Io",
    "Ai",
    "Bi",
    "Bs",
    "Ci",
    "Di",
    "Fi",
    "Gi",
    "Go",
    "Gr",
    "Hi",
    "Im",
    "Si",
    "Ti",
    "Tb",
    "Vsc",
    "Wi",
    "Cg",
    "Sl",
    "Rx",
  ].some((p) => name.startsWith(p));
};

// ─────────────────────────────────────────────
// Main Sidebar Component
// ─────────────────────────────────────────────

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { setTitle } = usePageTitle();

  const { canView, userRole } = usePermissions();

  // Determines whether Payslip routes to
  // admin approval queue or employee payslip page.
  const isHrAdmin =
    userRole === "Super Admin" ||
    userRole === "Branch Admin" ||
    userRole?.toLowerCase().includes("admin") ||
    userRole?.toLowerCase().includes("hr");

  // ───────────────────────────────────────────
  // State Variables
  // ───────────────────────────────────────────

  const [collapsed, setCollapsed] = useState(false);

  const [hoveredTooltip, setHoveredTooltip] = useState(null);

  const [tooltipPosition, setTooltipPosition] = useState({
    top: 0,
    left: 0,
  });

  const [hoveredSubmenuParent, setHoveredSubmenuParent] = useState(null);

  const [submenuTooltipPosition, setSubmenuTooltipPosition] = useState({
    top: 0,
    left: 0,
  });

  const leaveTimeoutRef = useRef(null);

  const [activeMainMenu, setActiveMainMenu] = useState(
    () => localStorage.getItem("activeMainMenu") || "dashboard",
  );

  const [activeSubMenu, setActiveSubMenu] = useState(
    () => localStorage.getItem("activeSubMenu") || null,
  );

  const [activeOpenMenu, setActiveOpenMenu] = useState(() => {
    const saved = localStorage.getItem("activeOpenMenu");

    return (
      saved ||
      ([
        "master",
        "leads",
        "hr",
        "inventory",
        "Craftsmen",
        "transport",
        "billing",
        "reports",
      ].includes(activeMainMenu)
        ? activeMainMenu
        : null)
    );
  });

  const isWarehousePage = location.pathname === "/warehouse";

  // ───────────────────────────────────────────
  // Effects
  // ───────────────────────────────────────────

  useEffect(() => {
    localStorage.setItem("activeMainMenu", activeMainMenu);

    localStorage.setItem("activeSubMenu", activeSubMenu || "");

    if (activeOpenMenu) {
      localStorage.setItem("activeOpenMenu", activeOpenMenu);
    } else {
      localStorage.removeItem("activeOpenMenu");
    }
  }, [activeMainMenu, activeSubMenu, activeOpenMenu]);

  useEffect(() => {
    if (
      location.pathname.includes("/billing/add") ||
      location.pathname.includes("/billing/edit")
    ) {
      setCollapsed(true);
    }
  }, [location.pathname]);

  // ───────────────────────────────────────────
  // Navigation Handlers
  // ───────────────────────────────────────────

  const handleMainClick = (
    menuKey,
    hasSubmenu = false,
    defaultPath = null,
    defaultTitle = null,
  ) => {
    setActiveMainMenu(menuKey);
    setActiveSubMenu(null);

    if (hasSubmenu) {
      setActiveOpenMenu(activeOpenMenu === menuKey ? null : menuKey);
    } else {
      setActiveOpenMenu(null);

      if (defaultPath) {
        navigate(defaultPath);

        let titleToSet = defaultTitle;

        if (!titleToSet) {
          titleToSet =
            menuKey === "billing"
              ? "Billing"
              : menuKey.charAt(0).toUpperCase() + menuKey.slice(1);
        }

        setTitle(titleToSet);
      }
    }
  };

  const goTo = (subKey, mainKey, path, title) => {
    setActiveSubMenu(subKey);
    setActiveMainMenu(mainKey);

    navigate(path);
    setTitle(title);

    if (window.innerWidth <= 768) {
      setActiveOpenMenu(null);
    }
  };

  const isOpen = (menuKey) => activeOpenMenu === menuKey;

  // ───────────────────────────────────────────
  // Tooltip Handlers
  // ───────────────────────────────────────────

  const handleMouseEnter = (e, text) => {
    if (collapsed) {
      const rect = e.currentTarget.getBoundingClientRect();

      setTooltipPosition({
        top: rect.top + rect.height / 2,
        left: rect.right + 10,
      });

      setHoveredTooltip(text);
    }
  };

  const handleMouseLeave = () => setHoveredTooltip(null);

  const handleSubmenuParentHover = (e, menuKey) => {
    if (collapsed) {
      if (leaveTimeoutRef.current) {
        clearTimeout(leaveTimeoutRef.current);

        leaveTimeoutRef.current = null;
      }

      const rect = e.currentTarget.getBoundingClientRect();

      setSubmenuTooltipPosition({
        top: rect.top,
        left: rect.right + 10,
      });

      setHoveredSubmenuParent(menuKey);
    }
  };

  const handleSubmenuParentLeave = () => {
    leaveTimeoutRef.current = setTimeout(() => {
      setHoveredSubmenuParent(null);
    }, 200);
  };

  const handleSubmenuTooltipEnter = () => {
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);

      leaveTimeoutRef.current = null;
    }
  };

  const handleSubmenuTooltipLeave = () => {
    setHoveredSubmenuParent(null);
  };

  // ───────────────────────────────────────────
  // Reports Navigation
  // ───────────────────────────────────────────

  const goToReport = (subKey, tabName, title) => {
    setActiveSubMenu(subKey);
    setActiveMainMenu("reports");

    navigate(`/reports?tab=${encodeURIComponent(tabName)}`);

    setTitle(title);

    if (window.innerWidth <= 768) {
      setActiveOpenMenu(null);
    }
  };

  // ───────────────────────────────────────────
  // Submenu Data
  // ───────────────────────────────────────────

  const submenuData = {
    // ─────────────────────────────────────────
    // MASTER
    // ─────────────────────────────────────────

    master: [
      {
        key: "Roles",
        label: "Roles",
        icon: Shield,
        path: "/master/roles",
        canView: canView("users"),
      },
      {
        key: "users",
        label: "Users",
        icon: UserCog,
        path: "/master/users",
        canView: canView("users"),
      },
      {
        key: "branch",
        label: "Branch Master",
        icon: Building2,
        path: "/master/branch",
        canView: userRole === "Super Admin",
      },
      {
        key: "Department",
        label: "Department",
        icon: Department,
        path: "/master/department",
        canView: canView("department"),
      },
      {
        key: "colors",
        label: "Colors",
        icon: Palette,
        path: "/master/colors",
        canView: canView("colors"),
      },
      {
        key: "DocumentManager",
        label: "Document",
        icon: FolderOpen,
        path: "/master/documentmanager",
        canView: canView("documents"),
      },
      {
        key: "remainder",
        label: "Remainder",
        icon: Clock,
        path: "/master/reminders",
        canView: canView("remainder"),
      },
      {
        key: "shift",
        label: "Shift",
        icon: Clock,
        path: "/master/shift",
        canView: canView("shift"),
      },
    ],

    // ─────────────────────────────────────────
    // LEADS
    // ─────────────────────────────────────────

    leads: [
      {
        key: "enquiry-list",
        label: "Enquiry List",
        icon: FileText,
        path: "/leads/enquiry-list",
        canView: canView("leads") || canView("enquiryList"),
      },
      {
        key: "new-enquiry",
        label: "New Enquiry",
        icon: FileQuestion,
        path: "/leads/new-enquiry",
        canView: canView("leads") || canView("newEnquiry"),
      },
      {
        key: "follow-up",
        label: "Follow Ups",
        icon: PhoneCall,
        path: "/leads/follow-up",
        canView: canView("leads") || canView("followUps"),
      },
      {
        key: "site-visit",
        label: "Site Visit",
        icon: MapPin,
        path: "/leads/site-visit",
        canView: canView("leads") || canView("siteVisit"),
      },
      // {
      //   key: "quotation-sent",
      //   label: "Quotation Sent",
      //   icon: Quotation,
      //   path: "/leads/quotation-sent",
      //   canView: canView("leads") || canView("quotation"),
      // },
      {
        key: "future",
        label: "Future Follow",
        icon: ClockIcon,
        path: "/leads/future",
        canView: canView("leads") || canView("future"),
      },
      {
        key: "estimate",
        label: "Estimate",
        icon: EstimateIcon,
        path: "/leads/estimate",
        canView: canView("leads") || canView("estimate"),
      },
      {
        key: "site-designer",
        label: "Site Designer",
        icon: SiteDesignerIcon,
        path: "/leads/site-designer",
        canView: canView("leads") || canView("siteDesigner"),
      },
    ],

    // ─────────────────────────────────────────
    // HR
    // ─────────────────────────────────────────

    hr: [
      {
        key: "employee-master",
        label: "Employee",
        icon: UserCheck,
        path: "/hr/employee-master",
        canView: canView("employeeMaster"),
      },
      {
        key: "attendances",
        label: "Attendance",
        icon: CalendarDays,
        path: "/hr/attendances",
        canView: canView("attendances"),
      },
      {
        key: "holidays",
        label: "Holiday",
        icon: CalendarOff,
        path: "/hr/holidays",
        canView: canView("holidays"),
      },
      {
        key: "allowance",
        label: "Allowance",
        icon: Wallet,
        path: "/hr/allowance",
        canView: canView("travelAllowance"),
      },
      {
        key: "leave-management",
        label: "Leave",
        icon: Leaf,
        path: "/hr/leave-management",
        canView: canView("leaveManagement"),
      },
      {
        key: "salary-management",
        label: "Salary",
        icon: Wallet,
        path: "/hr/salary-management",
        canView: canView("salaryManagement"),
      },
      {
        key: "employee-advance",
        label: "Advance",
        icon: CreditCard,
        path: "/hr/employee-advance",
        canView: canView("employeeAdvance"),
      },

      isHrAdmin
        ? {
            key: "payslip-requests",
            label: "Payslip",
            icon: CreditCard,
            path: "/hr/paysliprequest",
            canView: canView("payslip"),
          }
        : {
            key: "employee-payslip",
            label: "My Payslip",
            icon: CreditCard,
            path: "/employeepayslip",
            canView: canView("payslip"),
          },

      {
        key: "id-card",
        label: "ID Card",
        icon: IdCard,
        path: "/hr/id-card",
        canView: canView("idCard"),
      },
    ],

    // ─────────────────────────────────────────
    // INVENTORY (Catalog added as first item)
    // ─────────────────────────────────────────

    inventory: [
      {
        key: "catalog",
        label: "Catalog",
        icon: FolderOpen,
        path: "/catalog",
        canView: canView("catalog"),
      },
      {
        key: "product",
        label: "Product",
        icon: Package,
        path: "/inventory/product",
        canView: canView("product"),
      },
      {
        key: "purchase",
        label: "Purchase",
        icon: ShoppingBag,
        path: "/inventory/purchase",
        canView: canView("purchase"),
      },
      {
        key: "purchase-return",
        label: "Purchase Return",
        icon: RotateCcw,
        path: "/inventory/purchase-return",
        canView: canView("purchase"),
      },
      {
        key: "stock",
        label: "Stock",
        icon: Archive,
        path: "/inventory/stock",
        canView: canView("stock"),
      },
      {
        key: "stock-report",
        label: "Stock Report",
        icon: BarChart3,
        path: "/inventory/stock-report",
        canView: canView("stock"),
      },
      {
        key: "damage",
        label: "Damage",
        icon: AlertTriangle,
        path: "/inventory/damage",
        canView: canView("damage"),
      },
    ],

    // ─────────────────────────────────────────
    // CRAFTSMEN
    // ─────────────────────────────────────────

    Craftsmen: [
      {
        key: "Craftsmen-master",
        label: "Carpenter",
        icon: CarpenterIcon,
        path: "/craftsmen/craftsmen-master",
        canView: canView("carpenterMaster"),
      },
      {
        key: "job-assigned",
        label: "Job Assigned",
        icon: Wrench,
        path: "/craftsmen/job-assigned",
        canView: canView("jobAssigned"),
      },
      {
        key: "work-completion",
        label: "Work Done",
        icon: CheckCircle,
        path: "/craftsmen/work-completion",
        canView: canView("workCompletion"),
      },
      {
        key: "carpenteradvance",
        label: "Advance",
        icon: CreditCard,
        path: "/craftsmen/carpenteradvance",
        canView: canView("carpenteradvance"),
      },
      {
        key: "agents",
        label: "Team",
        hidden: true,
        icon: Users,
        path: "/craftsmen/agents",
        canView: canView("agents"),
      },
    ],
    // ─────────────────────────────────────────
    // TRANSPORT
    // ─────────────────────────────────────────

    transport: [
      {
        key: "vehicles",
        label: "Vehicles",
        icon: TruckIcon,
        path: "/transport/vehicles",
        canView: canView("transport") || canView("vehicles"),
      },
      {
        key: "drivers",
        label: "Drivers",
        icon: UserCircle,
        path: "/transport/drivers",
        canView: canView("transport") || canView("drivers"),
      },
      {
        key: "locations",
        label: "Locations",
        icon: MapPinned,
        path: "/transport/locations",
        canView: canView("transport") || canView("locations"),
      },
      {
        key: "shipments",
        label: "Shipments",
        icon: PackageCheck,
        path: "/transport/shipments",
        canView: canView("transport") || canView("shipments"),
      },
      {
        key: "shipment-history",
        label: "History",
        icon: History,
        path: "/transport/shipments/history",
        canView: canView("transport") || canView("shipmentHistory"),
      },
      {
        key: "live-tracking",
        label: "Live Tracking",
        icon: Radio,
        path: "/transport/shipments/live-tracking",
        canView: canView("transport") || canView("liveTracking"),
      },
      {
        key: "transport-salary",
        label: "Salary",
        icon: Wallet,
        path: "/transport/drivers/salary",
        canView: canView("transport") || canView("transportSalary"),
      },
      // ✅ Item #22: Mss Name master
      {
        key: "mss-name",
        label: "Mss Name",
        icon: Building2,
        path: "/transport/mss-name",
        canView: canView("transport") || canView("mssNames"),
      },
    ],

    // ─────────────────────────────────────────
    // BILLING
    // ─────────────────────────────────────────

    billing: [
      {
        key: "billing-list",
        label: "Billing List",
        icon: Receipt,
        path: "/billing",
        canView: canView("Billing"),
      },
      {
        key: "billing-add",
        label: "Add Billing",
        icon: Plus,
        path: "/billing/add",
        canView: canView("Billing"),
      },
      {
        key: "billing-pending",
        label: "Pending Billing",
        icon: ClockArrowUp,
        path: "/billing/pending-billing",
        canView: canView("Billing"),
      },
      {
        key: "balance-sheet",
        label: "Balance Sheet",
        icon: BarChart3,
        path: "/billing/balance-sheet",
        canView: canView("Billing"),
      },
      {
        key: "sales-ledger",
        label: "Ledger",
        icon: FileSpreadsheet,
        path: "/billing/sales-ledger",
        canView: canView("Billing"),
      },
      {
        key: "sale-return",
        label: "Sale Return",
        icon: RotateCcw,
        path: "/billing/sale-return",
        canView: canView("Billing"),
      },
    ],

    // ─────────────────────────────────────────
    // REPORTS
    // ─────────────────────────────────────────

    reports: [
      {
        key: "reports-overview",
        label: "Overview",
        icon: ReportsOverviewIcon,
        path: "/reports?tab=Overview",
        canView:
          canView("reportsSales") ||
          canView("reportsPurchase") ||
          canView("reportsStock") ||
          canView("reportsProducts") ||
          canView("reportsProject") ||
          canView("reportsLead") ||
          canView("reportsDealer") ||
          canView("reportsEmployee") ||
          canView("reportsAttendance") ||
          canView("reportsCarpenter") ||
          canView("reportsExpense") ||
          canView("reportsDamage") ||
          canView("reportsShipment") ||
          canView("reportsSalary") ||
          canView("reportsClients") ||
          canView("reportsTasks") ||
          canView("reportsPurchaseReturn") ||
          canView("reportsSaleReturn") ||
          canView("reportsLeave") ||
          canView("reportsAllowance") ||
          canView("reportsEmployeeAdvance") ||
          canView("reportsJobAssigned") ||
          canView("reportsWorkCompletion") ||
          canView("reportsVehicles") ||
          canView("reportsDrivers") ||
          canView("reportsDriverSalary") ||
          canView("reportsAssets") ||
          canView("reportsBalanceSheet") ||
          canView("reportsSalesLedger"),
      },
      {
        key: "reports-sales",
        label: "Sales",
        icon: TrendingUp,
        path: "/reports?tab=Sales",
        canView: canView("reportsSales"),
      },
      {
        key: "reports-purchase",
        label: "Purchase",
        icon: PurchaseIcon,
        path: "/reports?tab=Purchase",
        canView: canView("reportsPurchase"),
      },
      {
        key: "reports-stock",
        label: "Stock",
        icon: StockIcon,
        path: "/reports?tab=Stock",
        canView: canView("reportsStock"),
      },
      {
        key: "reports-products",
        label: "Products",
        icon: Boxes,
        path: "/reports?tab=Products",
        canView: canView("reportsProducts"),
      },
      {
        key: "reports-project",
        label: "Project",
        icon: ProjectIcon,
        path: "/reports?tab=Project",
        canView: canView("reportsProject"),
      },
      {
        key: "reports-lead",
        label: "Lead",
        icon: LeadIcon,
        path: "/reports?tab=Lead",
        canView: canView("reportsLead"),
      },
      {
        key: "reports-dealer",
        label: "Dealer",
        icon: DealerIcon,
        path: "/reports?tab=Dealer",
        canView: canView("reportsDealer"),
      },
      {
        key: "reports-employee",
        label: "Employee",
        icon: EmployeeIcon,
        path: "/reports?tab=Employee",
        canView: canView("reportsEmployee"),
      },
      {
        key: "reports-attendance",
        label: "Attendance",
        icon: AttendanceReportIcon,
        path: "/reports?tab=Attendance",
        canView: canView("reportsAttendance"),
      },
      {
        key: "reports-carpenter",
        label: "Carpenter",
        icon: CarpenterReportIcon,
        path: "/reports?tab=Carpenter",
        canView: canView("reportsCarpenter"),
      },
      {
        key: "reports-expense",
        label: "Expense",
        icon: ExpenseIcon,
        path: "/reports?tab=Expense",
        canView: canView("reportsExpense"),
      },
      {
        key: "reports-damage",
        label: "Damage",
        icon: DamageIcon,
        path: "/reports?tab=Damage",
        canView: canView("reportsDamage"),
      },
      {
        key: "reports-shipment",
        label: "Shipment",
        icon: ShipmentIcon,
        path: "/reports?tab=Shipment",
        canView: canView("reportsShipment"),
      },
      {
        key: "reports-salary",
        label: "Salary",
        icon: SalaryIcon,
        path: "/reports?tab=Salary",
        canView: canView("reportsSalary"),
      },
      {
        key: "reports-clients",
        label: "Clients",
        icon: Users2,
        path: "/reports?tab=Clients",
        canView: canView("reportsClients"),
      },
      {
        key: "reports-tasks",
        label: "Tasks",
        icon: ListTodo,
        path: "/reports?tab=Tasks",
        canView: canView("reportsTasks"),
      },
      {
        key: "reports-purchase-return",
        label: "Purchase Return",
        icon: RotateCcw,
        path: "/reports?tab=Purchase%20Return",
        canView: canView("reportsPurchaseReturn"),
      },
      {
        key: "reports-sale-return",
        label: "Sale Return",
        icon: RotateCcw,
        path: "/reports?tab=Sale%20Return",
        canView: canView("reportsSaleReturn"),
      },
      {
        key: "reports-leave",
        label: "Leave",
        icon: Leaf,
        path: "/reports?tab=Leave",
        canView: canView("reportsLeave"),
      },
      {
        key: "reports-allowance",
        label: "Allowance",
        icon: Wallet,
        path: "/reports?tab=Allowance",
        canView: canView("reportsAllowance"),
      },
      {
        key: "reports-employee-advance",
        label: "Employee Advance",
        icon: CreditCard,
        path: "/reports?tab=Employee%20Advance",
        canView: canView("reportsEmployeeAdvance"),
      },
      {
        key: "reports-job-assigned",
        label: "Job Assigned",
        icon: Wrench,
        path: "/reports?tab=Job%20Assigned",
        canView: canView("reportsJobAssigned"),
      },
      {
        key: "reports-work-completion",
        label: "Work Completion",
        icon: CheckCircle,
        path: "/reports?tab=Work%20Completion",
        canView: canView("reportsWorkCompletion"),
      },
      {
        key: "reports-vehicles",
        label: "Vehicles",
        icon: TruckIcon,
        path: "/reports?tab=Vehicles",
        canView: canView("reportsVehicles"),
      },
      {
        key: "reports-drivers",
        label: "Drivers",
        icon: UserCircle,
        path: "/reports?tab=Drivers",
        canView: canView("reportsDrivers"),
      },
      {
        key: "reports-driver-salary",
        label: "Driver Salary",
        icon: Wallet,
        path: "/reports?tab=Driver%20Salary",
        canView: canView("reportsDriverSalary"),
      },
      {
        key: "reports-assets",
        label: "Assets",
        icon: Landmark,
        path: "/reports?tab=Assets",
        canView: canView("reportsAssets"),
      },
      {
        key: "reports-balance-sheet",
        label: "Balance Sheet",
        icon: BalanceSheetIcon,
        path: "/reports?tab=Balance%20Sheet",
        canView: canView("reportsBalanceSheet"),
      },
      {
        key: "reports-sales-ledger",
        label: "Sales Ledger",
        icon: SalesLedgerIcon,
        path: "/reports?tab=Sales%20Ledger",
        canView: canView("reportsSalesLedger"),
      },
    ],
  };

  // ───────────────────────────────────────────
  // Sidebar Render
  // ───────────────────────────────────────────

  return (
    <>
      <aside
        className={`sidebar ${collapsed ? "collapsed" : ""} ${
          isWarehousePage ? "warehouse-sidebar" : ""
        }`}
      >
        {/* ─────────────────────────────────────
            SIDEBAR HEADER
            NOTE: matches CSS ".sidebar-top" /
            ".sidebar-logo-img" — the whole block
            is clickable and toggles collapse,
            exactly as ".sidebar-top { cursor:pointer }"
            in Sidebar.css implies.
        ───────────────────────────────────── */}

        <div
          className="sidebar-top"
          onClick={() => setCollapsed((prev) => !prev)}
          role="button"
          tabIndex={0}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <img src={Logo} alt="Harwin Logo" className="sidebar-logo-img" />
        </div>

        {/* ─────────────────────────────────────
            SIDEBAR BODY (SCROLLABLE)
            NOTE: renamed to "sidebar-scroll" to
            match Sidebar.css, which is what
            actually enables overflow-y: auto.
        ───────────────────────────────────── */}

        <div className="sidebar-scroll">
          <ul className="sidebar-menu">
            {/* ══════════════════════════════════
                MODULE 1: DASHBOARD
            ══════════════════════════════════ */}

            <li
              className={`menu-item ${
                activeMainMenu === "dashboard" ? "menu-active" : ""
              }`}
              onClick={() =>
                handleMainClick("dashboard", false, "/dashboard", "Dashboard")
              }
              onMouseEnter={(e) => handleMouseEnter(e, "Dashboard")}
              onMouseLeave={handleMouseLeave}
            >
              <House />

              {!collapsed && <span>Dashboard</span>}
            </li>

            {/* ══════════════════════════════════
                MODULE 2: MASTER
            ══════════════════════════════════ */}

            {(canView("users") ||
              userRole === "Super Admin" ||
              canView("brand") ||
              canView("department") ||
              canView("colors") ||
              canView("documents") ||
              canView("remainder") ||
              canView("shift")) && (
              <>
                <li
                  className={`menu-item ${
                    activeMainMenu === "master" ? "menu-active" : ""
                  }`}
                  onClick={() => handleMainClick("master", true)}
                  onMouseEnter={(e) => {
                    handleMouseEnter(e, "Master");

                    handleSubmenuParentHover(e, "master");
                  }}
                  onMouseLeave={() => {
                    handleMouseLeave();
                    handleSubmenuParentLeave();
                  }}
                >
                  <Layers />

                  {!collapsed && (
                    <>
                      <span>Master</span>

                      <ChevronDown
                        className={`dropdown-arrow ${
                          isOpen("master") ? "open" : ""
                        }`}
                      />
                    </>
                  )}
                </li>

                {isOpen("master") && !collapsed && (
                  <ul className="submenu">
                    {canView("users") && (
                      <li
                        className={
                          activeSubMenu === "Roles" ? "submenu-active" : ""
                        }
                        onClick={() =>
                          goTo("Roles", "master", "/master/roles", "Roles")
                        }
                      >
                        <Shield className="submenu-icon" />
                        <span>Roles</span>
                      </li>
                    )}

                    {canView("users") && (
                      <li
                        className={
                          activeSubMenu === "users" ? "submenu-active" : ""
                        }
                        onClick={() =>
                          goTo("users", "master", "/master/users", "Users")
                        }
                      >
                        <UserCog className="submenu-icon" />
                        <span>Users</span>
                      </li>
                    )}

                    {userRole === "Super Admin" && (
                      <li
                        className={
                          activeSubMenu === "branch" ? "submenu-active" : ""
                        }
                        onClick={() =>
                          goTo(
                            "branch",
                            "master",
                            "/master/branch",
                            "Branch Master",
                          )
                        }
                      >
                        <Building2 className="submenu-icon" />
                        <span>Branch Master</span>
                      </li>
                    )}

                    {canView("brand") && (
                      <li
                        className={
                          activeSubMenu === "brand" ? "submenu-active" : ""
                        }
                        onClick={() =>
                          goTo(
                            "brand",
                            "master",
                            "/master/brand",
                            "Brand Master",
                          )
                        }
                      >
                        <SquareMousePointer className="submenu-icon" />
                        <span>Brand Master</span>
                      </li>
                    )}

                    {canView("department") && (
                      <li
                        className={
                          activeSubMenu === "Department" ? "submenu-active" : ""
                        }
                        onClick={() =>
                          goTo(
                            "Department",
                            "master",
                            "/master/department",
                            "Department",
                          )
                        }
                      >
                        <Department className="submenu-icon" />
                        <span>Department</span>
                      </li>
                    )}

                    {canView("colors") && (
                      <li
                        className={
                          activeSubMenu === "colors" ? "submenu-active" : ""
                        }
                        onClick={() =>
                          goTo(
                            "colors",
                            "master",
                            "/master/colors",
                            "Colour Catalogue",
                          )
                        }
                      >
                        <Paintbrush className="submenu-icon" />
                        <span>Colors</span>
                      </li>
                    )}

                    {canView("documents") && (
                      <li
                        className={
                          activeSubMenu === "DocumentManager"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goTo(
                            "DocumentManager",
                            "master",
                            "/master/documentmanager",
                            "Document",
                          )
                        }
                      >
                        <FolderOpen className="submenu-icon" />
                        <span>Documents</span>
                      </li>
                    )}

                    {canView("remainder") && (
                      <li
                        className={
                          activeSubMenu === "remainder" ? "submenu-active" : ""
                        }
                        onClick={() =>
                          goTo(
                            "remainder",
                            "master",
                            "/master/reminders",
                            "Remainder",
                          )
                        }
                      >
                        <Clock className="submenu-icon" />
                        <span>Remainder</span>
                      </li>
                    )}

                    {canView("shift") && (
                      <li
                        className={
                          activeSubMenu === "shift" ? "submenu-active" : ""
                        }
                        onClick={() =>
                          goTo("shift", "master", "/master/shift", "shift")
                        }
                      >
                        <Clock className="submenu-icon" />
                        <span>Shift</span>
                      </li>
                    )}
                  </ul>
                )}
              </>
            )}

            {/* ══════════════════════════════════
                MODULE 3: LEADS
            ══════════════════════════════════ */}

            {(canView("leads") ||
              canView("enquiryList") ||
              canView("newEnquiry") ||
              canView("followUps") ||
              canView("siteVisit") ||
              // canView("quotation") ||
              canView("future") ||
              canView("estimate") ||
              canView("siteDesigner")) && (
              <>
                <li
                  className={`menu-item ${
                    activeMainMenu === "leads" ? "menu-active" : ""
                  }`}
                  onClick={() => handleMainClick("leads", true)}
                  onMouseEnter={(e) => {
                    handleMouseEnter(e, "Leads Management");

                    handleSubmenuParentHover(e, "leads");
                  }}
                  onMouseLeave={() => {
                    handleMouseLeave();
                    handleSubmenuParentLeave();
                  }}
                >
                  <Briefcase />

                  {!collapsed && (
                    <>
                      <span>Leads</span>

                      <ChevronDown
                        className={`dropdown-arrow ${
                          isOpen("leads") ? "open" : ""
                        }`}
                      />
                    </>
                  )}
                </li>
                {isOpen("leads") && !collapsed && (
                  <ul className="submenu">
                    {(canView("leads") || canView("enquiryList")) && (
                      <li
                        className={
                          activeSubMenu === "enquiry-list"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goTo(
                            "enquiry-list",
                            "leads",
                            "/leads/enquiry-list",
                            "Enquiry List",
                          )
                        }
                      >
                        <FileText className="submenu-icon" />
                        <span>Enquiry List</span>
                      </li>
                    )}

                    {(canView("leads") || canView("newEnquiry")) && (
                      <li
                        className={
                          activeSubMenu === "new-enquiry"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goTo(
                            "new-enquiry",
                            "leads",
                            "/leads/new-enquiry",
                            "New Enquiry",
                          )
                        }
                      >
                        <FileQuestion className="submenu-icon" />
                        <span>New Enquiry</span>
                      </li>
                    )}

                    {(canView("leads") || canView("followUps")) && (
                      <li
                        className={
                          activeSubMenu === "follow-up" ? "submenu-active" : ""
                        }
                        onClick={() =>
                          goTo(
                            "follow-up",
                            "leads",
                            "/leads/follow-up",
                            "Follow Ups",
                          )
                        }
                      >
                        <PhoneCall className="submenu-icon" />
                        <span>Follow Ups</span>
                      </li>
                    )}

                    {(canView("leads") || canView("siteVisit")) && (
                      <li
                        className={
                          activeSubMenu === "site-visit" ? "submenu-active" : ""
                        }
                        onClick={() =>
                          goTo(
                            "site-visit",
                            "leads",
                            "/leads/site-visit",
                            "Site Visit",
                          )
                        }
                      >
                        <MapPin className="submenu-icon" />
                        <span>Site Visit</span>
                      </li>
                    )}

                    {/* {(canView("leads") || canView("quotation")) && (
                      <li
                        className={
                          activeSubMenu === "quotation-sent"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goTo(
                            "quotation-sent",
                            "leads",
                            "/leads/quotation-sent",
                            "Quotation Sent",
                          )
                        }
                      >
                        <Quotation className="submenu-icon" />
                        <span>Quotation Sent</span>
                      </li>
                    )} */}

                    {(canView("leads") || canView("future")) && (
                      <li
                        className={
                          activeSubMenu === "future" ? "submenu-active" : ""
                        }
                        onClick={() =>
                          goTo(
                            "future",
                            "leads",
                            "/leads/future",
                            "Future Follow Up",
                          )
                        }
                      >
                        <ClockIcon className="submenu-icon" />
                        <span>Future Follow</span>
                      </li>
                    )}

                    {(canView("leads") || canView("estimate")) && (
                      <li
                        className={
                          activeSubMenu === "estimate" ? "submenu-active" : ""
                        }
                        onClick={() =>
                          goTo(
                            "estimate",
                            "leads",
                            "/leads/estimate",
                            "Estimate",
                          )
                        }
                      >
                        <EstimateIcon className="submenu-icon" />
                        <span>Estimate</span>
                      </li>
                    )}

                    {(canView("leads") || canView("siteDesigner")) && (
                      <li
                        className={
                          activeSubMenu === "site-designer"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goTo(
                            "site-designer",
                            "leads",
                            "/leads/site-designer",
                            "Site Designer",
                          )
                        }
                      >
                        <SiteDesignerIcon className="submenu-icon" />
                        <span>Site Designer</span>
                      </li>
                    )}
                  </ul>
                )}
              </>
            )}

            {/* ════════════════════════════════════════════════════ */}
            {/* MODULE 4: HR MANAGEMENT */}
            {/* ════════════════════════════════════════════════════ */}

            {(canView("employeeMaster") ||
              canView("attendances") ||
              canView("holidays") ||
              canView("travelAllowance") ||
              canView("leaveManagement") ||
              canView("salaryManagement") ||
              canView("payslip") ||
              canView("employeeAdvance") ||
              canView("idCard")) && (
              <>
                <li
                  className={`menu-item ${
                    activeMainMenu === "hr" ? "menu-active" : ""
                  }`}
                  onClick={() => handleMainClick("hr", true)}
                  onMouseEnter={(e) => {
                    handleMouseEnter(e, "HR Management");
                    handleSubmenuParentHover(e, "hr");
                  }}
                  onMouseLeave={() => {
                    handleMouseLeave();
                    handleSubmenuParentLeave();
                  }}
                >
                  <Users />

                  {!collapsed && (
                    <>
                      <span>HR Management</span>
                      <ChevronDown
                        className={`dropdown-arrow ${
                          isOpen("hr") ? "open" : ""
                        }`}
                      />
                    </>
                  )}
                </li>

                {isOpen("hr") && !collapsed && (
                  <ul className="submenu">
                    {canView("employeeMaster") && (
                      <li
                        className={
                          activeSubMenu === "employee-master"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goTo(
                            "employee-master",
                            "hr",
                            "/hr/employee-master",
                            "Employee Master",
                          )
                        }
                      >
                        <UserCheck className="submenu-icon" />
                        <span>Employee</span>
                      </li>
                    )}

                    {canView("attendances") && (
                      <li
                        className={
                          activeSubMenu === "attendances"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goTo(
                            "attendances",
                            "hr",
                            "/hr/attendances",
                            "Attendances",
                          )
                        }
                      >
                        <CalendarDays className="submenu-icon" />
                        <span>Attendance</span>
                      </li>
                    )}

                    {canView("holidays") && (
                      <li
                        className={
                          activeSubMenu === "holidays" ? "submenu-active" : ""
                        }
                        onClick={() =>
                          goTo("holidays", "hr", "/hr/holidays", "Holiday")
                        }
                      >
                        <CalendarOff className="submenu-icon" />
                        <span>Holiday</span>
                      </li>
                    )}

                    {canView("travelAllowance") && (
                      <li
                        className={
                          activeSubMenu === "allowance" ? "submenu-active" : ""
                        }
                        onClick={() =>
                          goTo("allowance", "hr", "/hr/allowance", "Allowance")
                        }
                      >
                        <Wallet className="submenu-icon" />
                        <span>Allowance</span>
                      </li>
                    )}

                    {canView("leaveManagement") && (
                      <li
                        className={
                          activeSubMenu === "leave-management"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goTo(
                            "leave-management",
                            "hr",
                            "/hr/leave-management",
                            "Leave Management",
                          )
                        }
                      >
                        <Leaf className="submenu-icon" />
                        <span>Leave</span>
                      </li>
                    )}

                    {canView("salaryManagement") && (
                      <li
                        className={
                          activeSubMenu === "salary-management"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goTo(
                            "salary-management",
                            "hr",
                            "/hr/salary-management",
                            "Salary Management",
                          )
                        }
                      >
                        <Wallet className="submenu-icon" />
                        <span>Salary</span>
                      </li>
                    )}

                    {canView("employeeAdvance") && (
                      <li
                        className={
                          activeSubMenu === "employee-advance"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goTo(
                            "employee-advance",
                            "hr",
                            "/hr/employee-advance",
                            "Employee Advance",
                          )
                        }
                      >
                        <CreditCard className="submenu-icon" />
                        <span>Advance</span>
                      </li>
                    )}

                    {canView("payslip") &&
                      (isHrAdmin ? (
                        <li
                          className={
                            activeSubMenu === "payslip-requests"
                              ? "submenu-active"
                              : ""
                          }
                          onClick={() =>
                            goTo(
                              "payslip-requests",
                              "hr",
                              "/hr/paysliprequest",
                              "Payslip",
                            )
                          }
                        >
                          <CreditCard className="submenu-icon" />
                          <span>Payslip</span>
                        </li>
                      ) : (
                        <li
                          className={
                            activeSubMenu === "employee-payslip"
                              ? "submenu-active"
                              : ""
                          }
                          onClick={() =>
                            goTo(
                              "employee-payslip",
                              "hr",
                              "/employeepayslip",
                              "My Payslip",
                            )
                          }
                        >
                          <CreditCard className="submenu-icon" />
                          <span>My Payslip</span>
                        </li>
                      ))}

                    {canView("idCard") && (
                      <li
                        className={
                          activeSubMenu === "id-card" ? "submenu-active" : ""
                        }
                        onClick={() =>
                          goTo("id-card", "hr", "/hr/id-card", "ID Card")
                        }
                      >
                        <IdCard className="submenu-icon" />
                        <span>ID Card</span>
                      </li>
                    )}
                  </ul>
                )}
              </>
            )}

            {/* ════════════════════════════════════════════════════ */}
            {/* MODULE 5: TASK MANAGEMENT */}
            {/* ════════════════════════════════════════════════════ */}

            {canView("taskManagement") && (
              <li
                className={`menu-item ${
                  activeMainMenu === "task-module" ? "menu-active" : ""
                }`}
                onClick={() =>
                  handleMainClick(
                    "task-module",
                    false,
                    "/hr/task-management",
                    "Task Management",
                  )
                }
                onMouseEnter={(e) => handleMouseEnter(e, "Task Management")}
                onMouseLeave={handleMouseLeave}
              >
                <ListTodo />

                {!collapsed && <span>Task Management</span>}
              </li>
            )}

            {/* ════════════════════════════════════════════════════ */}
            {/* MODULE 6: CLIENTS */}
            {/* ════════════════════════════════════════════════════ */}

            {canView("clients") && (
              <li
                className={`menu-item ${
                  activeMainMenu === "clients" ? "menu-active" : ""
                }`}
                onClick={() =>
                  handleMainClick("clients", false, "/clients", "Clients")
                }
                onMouseEnter={(e) => handleMouseEnter(e, "Clients")}
                onMouseLeave={handleMouseLeave}
              >
                <Users2 />

                {!collapsed && <span>Clients</span>}
              </li>
            )}

            {/* ════════════════════════════════════════════════════ */}
            {/* MODULE 7: ORDERS (was Dealer & Distributor — Distributor/Trader removed) */}
            {/* ════════════════════════════════════════════════════ */}

            {(canView("dealerOrders") || canView("dealer")) && (
              <li
                className={`menu-item ${
                  activeMainMenu === "distributor-orders" ? "menu-active" : ""
                }`}
                onClick={() =>
                  handleMainClick(
                    "distributor-orders",
                    false,
                    "/distributor-orders",
                    "Orders",
                  )
                }
                onMouseEnter={(e) => handleMouseEnter(e, "Orders")}
                onMouseLeave={handleMouseLeave}
              >
                <Target />

                {!collapsed && <span>Orders</span>}
              </li>
            )}

            {/* ════════════════════════════════════════════════════ */}
            {/* MODULE 8: PROJECTS */}
            {/* ════════════════════════════════════════════════════ */}

            {canView("projects") && (
              <li
                className={`menu-item ${
                  activeMainMenu === "projects" ? "menu-active" : ""
                }`}
                onClick={() =>
                  handleMainClick("projects", false, "/projects", "Projects")
                }
                onMouseEnter={(e) => handleMouseEnter(e, "Projects")}
                onMouseLeave={handleMouseLeave}
              >
                <FolderKanban />

                {!collapsed && <span>Projects</span>}
              </li>
            )}

            {/* ════════════════════════════════════════════════════ */}
            {/* MODULE 9: CRAFTSMEN */}
            {/* ════════════════════════════════════════════════════ */}

            {(canView("carpenterMaster") ||
              canView("jobAssigned") ||
              canView("workCompletion") ||
              canView("carpenteradvance") ||
              canView("agents")) && (
              <>
                <li
                  className={`menu-item ${
                    activeMainMenu === "Craftsmen" ? "menu-active" : ""
                  }`}
                  onClick={() => handleMainClick("Craftsmen", true)}
                  onMouseEnter={(e) => {
                    handleMouseEnter(e, "Craftsmen");
                    handleSubmenuParentHover(e, "Craftsmen");
                  }}
                  onMouseLeave={() => {
                    handleMouseLeave();
                    handleSubmenuParentLeave();
                  }}
                >
                  <Hammer />

                  {!collapsed && (
                    <>
                      <span>Carpenter</span>
                      <ChevronDown
                        className={`dropdown-arrow ${
                          isOpen("Craftsmen") ? "open" : ""
                        }`}
                      />
                    </>
                  )}
                </li>

                {isOpen("Craftsmen") && !collapsed && (
                  <ul className="submenu">
                    {canView("carpenterMaster") && (
                      <li
                        className={
                          activeSubMenu === "Craftsmen-master"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goTo(
                            "Craftsmen-master",
                            "Craftsmen",
                            "/craftsmen/craftsmen-master",
                            "Carpenter Master",
                          )
                        }
                      >
                        <CarpenterIcon className="submenu-icon" />
                        <span>Carpenter</span>
                      </li>
                    )}

                    {canView("stock") && (
                      <li
                        className={
                          activeSubMenu === "carpenter-material-issue"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goTo(
                            "carpenter-material-issue",
                            "Craftsmen",
                            "/craftsmen/material-issue",
                            "Material Issue",
                          )
                        }
                      >
                        <Package className="submenu-icon" />
                        <span>Material Issue</span>
                      </li>
                    )}

                    {canView("jobAssigned") && (
                      <li
                        className={
                          activeSubMenu === "job-assigned"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goTo(
                            "job-assigned",
                            "Craftsmen",
                            "/craftsmen/job-assigned",
                            "Job Assigned",
                          )
                        }
                      >
                        <Wrench className="submenu-icon" />
                        <span>Job Assigned</span>
                      </li>
                    )}

                    {canView("workCompletion") && (
                      <li
                        className={
                          activeSubMenu === "work-completion"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goTo(
                            "work-completion",
                            "Craftsmen",
                            "/craftsmen/work-completion",
                            "Work Completion",
                          )
                        }
                      >
                        <CheckCircle className="submenu-icon" />
                        <span>Work Done</span>
                      </li>
                    )}

                    {canView("carpenteradvance") && (
                      <li
                        className={
                          activeSubMenu === "carpenteradvance"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goTo(
                            "carpenteradvance",
                            "Craftsmen",
                            "/craftsmen/carpenteradvance",
                            "Carpenter Advance",
                          )
                        }
                      >
                        <CreditCard className="submenu-icon" />
                        <span>Advance</span>
                      </li>
                    )}

                    {canView("agents") && (
                      <li
                        className={
                          activeSubMenu === "agents" ? "submenu-active" : ""
                        }
                        onClick={() =>
                          goTo(
                            "agents",
                            "Craftsmen",
                            "/craftsmen/agents",
                            "Agents",
                          )
                        }
                      >
                        <Users className="submenu-icon" />
                        <span>Team</span>
                      </li>
                    )}
                  </ul>
                )}
              </>
            )}

            {/* ════════════════════════════════════════════════════ */}
            {/* MODULE 10: INVENTORY */}
            {/* ════════════════════════════════════════════════════ */}

            {(canView("catalog") ||
              canView("product") ||
              canView("purchase") ||
              canView("stock") ||
              canView("damage") ||
              canView("OrderDetail")) && (
              <>
                <li
                  className={`menu-item ${
                    activeMainMenu === "inventory" ? "menu-active" : ""
                  }`}
                  onClick={() => handleMainClick("inventory", true)}
                  onMouseEnter={(e) => {
                    handleMouseEnter(e, "Inventory");
                    handleSubmenuParentHover(e, "inventory");
                  }}
                  onMouseLeave={() => {
                    handleMouseLeave();
                    handleSubmenuParentLeave();
                  }}
                >
                  <Boxes />

                  {!collapsed && (
                    <>
                      <span>Inventory</span>
                      <ChevronDown
                        className={`dropdown-arrow ${
                          isOpen("inventory") ? "open" : ""
                        }`}
                      />
                    </>
                  )}
                </li>

                {isOpen("inventory") && !collapsed && (
                  <ul className="submenu">
                    {canView("catalog") && (
                      <li
                        className={
                          activeSubMenu === "catalog" ? "submenu-active" : ""
                        }
                        onClick={() =>
                          goTo("catalog", "inventory", "/catalog", "Catalog")
                        }
                      >
                        <FolderOpen className="submenu-icon" />
                        <span>Catalog</span>
                      </li>
                    )}

                    {canView("product") && (
                      <li
                        className={
                          activeSubMenu === "product" ? "submenu-active" : ""
                        }
                        onClick={() =>
                          goTo(
                            "product",
                            "inventory",
                            "/inventory/product",
                            "Product",
                          )
                        }
                      >
                        <Package className="submenu-icon" />
                        <span>Product</span>
                      </li>
                    )}

                    {canView("purchase") && (
                      <>
                        <li
                          className={
                            activeSubMenu === "purchase" ? "submenu-active" : ""
                          }
                          onClick={() =>
                            goTo(
                              "purchase",
                              "inventory",
                              "/inventory/purchase",
                              "Purchase",
                            )
                          }
                        >
                          <ShoppingBag className="submenu-icon" />
                          <span>Purchase</span>
                        </li>

                        <li
                          className={
                            activeSubMenu === "purchase-return"
                              ? "submenu-active"
                              : ""
                          }
                          onClick={() =>
                            goTo(
                              "purchase-return",
                              "inventory",
                              "/inventory/purchase-return",
                              "Purchase Return",
                            )
                          }
                        >
                          <RotateCcw className="submenu-icon" />
                          <span>Pur Return</span>
                        </li>
                      </>
                    )}

                    {canView("stock") && (
                      <li
                        className={
                          activeSubMenu === "stock" ? "submenu-active" : ""
                        }
                        onClick={() =>
                          goTo(
                            "stock",
                            "inventory",
                            "/inventory/stock",
                            "Stock",
                          )
                        }
                      >
                        <Archive className="submenu-icon" />
                        <span>Stock</span>
                      </li>
                    )}

                    {canView("stock") && (
                      <li
                        className={
                          activeSubMenu === "stock-report"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goTo(
                            "stock-report",
                            "inventory",
                            "/inventory/stock-report",
                            "Stock Report",
                          )
                        }
                      >
                        <BarChart3 className="submenu-icon" />
                        <span>Stock Report</span>
                      </li>
                    )}

                    {canView("damage") && (
                      <li
                        className={
                          activeSubMenu === "damage" ? "submenu-active" : ""
                        }
                        onClick={() =>
                          goTo(
                            "damage",
                            "inventory",
                            "/inventory/damage",
                            "Damage",
                          )
                        }
                      >
                        <AlertTriangle className="submenu-icon" />
                        <span>Damage</span>
                      </li>
                    )}
                  </ul>
                )}
              </>
            )}

            {/* ════════════════════════════════════════════════════ */}
            {/* MODULE 11: WAREHOUSE */}
            {/* ════════════════════════════════════════════════════ */}

            {canView("warehouse") && (
              <li
                className={`menu-item ${
                  activeMainMenu === "warehouse" ? "menu-active" : ""
                }`}
                onClick={() =>
                  handleMainClick("warehouse", false, "/warehouse", "Warehouse")
                }
                onMouseEnter={(e) => handleMouseEnter(e, "Warehouse")}
                onMouseLeave={handleMouseLeave}
              >
                <Warehouse />

                {!collapsed && <span>Warehouse</span>}
              </li>
            )}

            {/* ════════════════════════════════════════════════════ */}
            {/* MODULE 12: TRANSPORT */}
            {/* ════════════════════════════════════════════════════ */}

            {(canView("transport") ||
              canView("vehicles") ||
              canView("drivers") ||
              canView("locations") ||
              canView("shipments") ||
              canView("shipmentHistory") ||
              canView("liveTracking") ||
              canView("transportSalary")) && (
              <>
                <li
                  className={`menu-item ${
                    activeMainMenu === "transport" ? "menu-active" : ""
                  }`}
                  onClick={() => handleMainClick("transport", true)}
                  onMouseEnter={(e) => {
                    handleMouseEnter(e, "Transport");
                    handleSubmenuParentHover(e, "transport");
                  }}
                  onMouseLeave={() => {
                    handleMouseLeave();
                    handleSubmenuParentLeave();
                  }}
                >
                  <Truck />

                  {!collapsed && (
                    <>
                      <span>Transport</span>
                      <ChevronDown
                        className={`dropdown-arrow ${
                          isOpen("transport") ? "open" : ""
                        }`}
                      />
                    </>
                  )}
                </li>

                {isOpen("transport") && !collapsed && (
                  <ul className="submenu">
                    {(canView("transport") || canView("vehicles")) && (
                      <li
                        className={
                          activeSubMenu === "vehicles" ? "submenu-active" : ""
                        }
                        onClick={() =>
                          goTo(
                            "vehicles",
                            "transport",
                            "/transport/vehicles",
                            "Vehicles",
                          )
                        }
                      >
                        <TruckIcon className="submenu-icon" />
                        <span>Vehicles</span>
                      </li>
                    )}

                    {(canView("transport") || canView("drivers")) && (
                      <li
                        className={
                          activeSubMenu === "drivers" ? "submenu-active" : ""
                        }
                        onClick={() =>
                          goTo(
                            "drivers",
                            "transport",
                            "/transport/drivers",
                            "Drivers",
                          )
                        }
                      >
                        <UserCircle className="submenu-icon" />
                        <span>Drivers</span>
                      </li>
                    )}

                    {(canView("transport") || canView("locations")) && (
                      <li
                        className={
                          activeSubMenu === "locations" ? "submenu-active" : ""
                        }
                        onClick={() =>
                          goTo(
                            "locations",
                            "transport",
                            "/transport/locations",
                            "Locations",
                          )
                        }
                      >
                        <MapPinned className="submenu-icon" />
                        <span>Locations</span>
                      </li>
                    )}

                    {(canView("transport") || canView("shipments")) && (
                      <li
                        className={
                          activeSubMenu === "shipments" ? "submenu-active" : ""
                        }
                        onClick={() =>
                          goTo(
                            "shipments",
                            "transport",
                            "/transport/shipments",
                            "Shipments",
                          )
                        }
                      >
                        <PackageCheck className="submenu-icon" />
                        <span>Shipments</span>
                      </li>
                    )}

                    {(canView("transport") || canView("shipmentHistory")) && (
                      <li
                        className={
                          activeSubMenu === "shipment-history"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goTo(
                            "shipment-history",
                            "transport",
                            "/transport/shipments/history",
                            "Shipment History",
                          )
                        }
                      >
                        <History className="submenu-icon" />
                        <span>History</span>
                      </li>
                    )}

                    {(canView("transport") || canView("liveTracking")) && (
                      <li
                        className={
                          activeSubMenu === "live-tracking"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goTo(
                            "live-tracking",
                            "transport",
                            "/transport/shipments/live-tracking",
                            "Live Tracking",
                          )
                        }
                      >
                        <Radio className="submenu-icon" />
                        <span>Live Tracking</span>
                      </li>
                    )}

                    {(canView("transport") || canView("transportSalary")) && (
                      <li
                        className={
                          activeSubMenu === "transport-salary"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goTo(
                            "transport-salary",
                            "transport",
                            "/transport/drivers/salary",
                            "Transport Salary",
                          )
                        }
                      >
                        <Wallet className="submenu-icon" />
                        <span>Salary</span>
                      </li>
                    )}
                  </ul>
                )}
              </>
            )}
            {/* ════════════════════════════════════════════════════ */}
            {/* MODULE 13: BILLING */}
            {/* ════════════════════════════════════════════════════ */}

            {canView("Billing") && (
              <>
                <li
                  className={`menu-item ${
                    activeMainMenu === "billing" ? "menu-active" : ""
                  }`}
                  onClick={() => handleMainClick("billing", true)}
                  onMouseEnter={(e) => {
                    handleMouseEnter(e, "Billing");
                    handleSubmenuParentHover(e, "billing");
                  }}
                  onMouseLeave={() => {
                    handleMouseLeave();
                    handleSubmenuParentLeave();
                  }}
                >
                  <IndianRupee />

                  {!collapsed && (
                    <>
                      <span>Billing</span>
                      <ChevronDown
                        className={`dropdown-arrow ${
                          isOpen("billing") ? "open" : ""
                        }`}
                      />
                    </>
                  )}
                </li>

                {isOpen("billing") && !collapsed && (
                  <ul className="submenu">
                    <li
                      className={
                        activeSubMenu === "billing-list" ? "submenu-active" : ""
                      }
                      onClick={() =>
                        goTo(
                          "billing-list",
                          "billing",
                          "/billing",
                          "Billing List",
                        )
                      }
                    >
                      <Receipt className="submenu-icon" />
                      <span>Billing List</span>
                    </li>

                    <li
                      className={
                        activeSubMenu === "billing-add" ? "submenu-active" : ""
                      }
                      onClick={() =>
                        goTo(
                          "billing-add",
                          "billing",
                          "/billing/add",
                          "Add Billing",
                        )
                      }
                    >
                      <Plus className="submenu-icon" />
                      <span>Add Billing</span>
                    </li>

                    <li
                      className={
                        activeSubMenu === "billing-pending"
                          ? "submenu-active"
                          : ""
                      }
                      onClick={() =>
                        goTo(
                          "billing-pending",
                          "billing",
                          "/billing/pending-billing",
                          "Pending Billing",
                        )
                      }
                    >
                      <ClockArrowUp className="submenu-icon" />
                      <span>Pending Billing</span>
                    </li>

                    <li
                      className={
                        activeSubMenu === "balance-sheet"
                          ? "submenu-active"
                          : ""
                      }
                      onClick={() =>
                        goTo(
                          "balance-sheet",
                          "billing",
                          "/billing/balance-sheet",
                          "Balance Sheet",
                        )
                      }
                    >
                      <BarChart3 className="submenu-icon" />
                      <span>Balance Sheet</span>
                    </li>

                    <li
                      className={
                        activeSubMenu === "sales-ledger" ? "submenu-active" : ""
                      }
                      onClick={() =>
                        goTo(
                          "sales-ledger",
                          "billing",
                          "/billing/sales-ledger",
                          "Ledger",
                        )
                      }
                    >
                      <FileSpreadsheet className="submenu-icon" />
                      <span>Ledger</span>
                    </li>

                    <li
                      className={
                        activeSubMenu === "client-sales-history"
                          ? "submenu-active"
                          : ""
                      }
                      onClick={() =>
                        goTo(
                          "client-sales-history",
                          "billing",
                          "/billing/client-sales-history",
                          "Client Sales History",
                        )
                      }
                    >
                      <WalletCards className="submenu-icon" />
                      <span>Recipient</span>
                    </li>

                    <li
                      className={
                        activeSubMenu === "sale-return" ? "submenu-active" : ""
                      }
                      onClick={() =>
                        goTo(
                          "sale-return",
                          "billing",
                          "/billing/sale-return",
                          "Sale Return",
                        )
                      }
                    >
                      <RotateCcw className="submenu-icon" />
                      <span>Sale Return</span>
                    </li>
                  </ul>
                )}
              </>
            )}

            {/* ════════════════════════════════════════════════════ */}
            {/* MODULE 14: DIGITAL MARKETING */}
            {/* ════════════════════════════════════════════════════ */}

            {canView("digitalmarketing") && (
              <li
                className={`menu-item ${
                  activeMainMenu === "digitalmarketing" ? "menu-active" : ""
                }`}
                onClick={() =>
                  handleMainClick(
                    "digitalmarketing",
                    false,
                    "/digitalmarketing",
                    "Digital Marketing",
                  )
                }
                onMouseEnter={(e) => handleMouseEnter(e, "Digital Marketing")}
                onMouseLeave={handleMouseLeave}
              >
                <RiAdvertisementLine />

                {!collapsed && <span>Digital Marketing</span>}
              </li>
            )}

            {/* ════════════════════════════════════════════════════ */}
            {/* MODULE 15: ASSETS */}
            {/* ════════════════════════════════════════════════════ */}

            {canView("assets") && (
              <li
                className={`menu-item ${
                  activeMainMenu === "assets" ? "menu-active" : ""
                }`}
                onClick={() =>
                  handleMainClick("assets", false, "/assets", "Assets")
                }
                onMouseEnter={(e) => handleMouseEnter(e, "Assets")}
                onMouseLeave={handleMouseLeave}
              >
                <Landmark />

                {!collapsed && <span>Assets</span>}
              </li>
            )}

            {/* ════════════════════════════════════════════════════ */}
            {/* MODULE 16: EXPENSES */}
            {/* ════════════════════════════════════════════════════ */}

            {canView("expenses") && (
              <li
                className={`menu-item ${
                  activeMainMenu === "expenses" ? "menu-active" : ""
                }`}
                onClick={() =>
                  handleMainClick("expenses", false, "/expenses", "Expenses")
                }
                onMouseEnter={(e) => handleMouseEnter(e, "Expenses")}
                onMouseLeave={handleMouseLeave}
              >
                <Receipt />

                {!collapsed && <span>Expenses</span>}
              </li>
            )}

            {/* ════════════════════════════════════════════════════ */}
            {/* MODULE 17: CCTV CAMERA */}
            {/* ════════════════════════════════════════════════════ */}

            {/* {canView("cctv") && (
              <li
                className={`menu-item ${
                  activeMainMenu === "hikvision"
                    ? "menu-active"
                    : ""
                }`}
                onClick={() =>
                  handleMainClick(
                    "hikvision",
                    false,
                    "/camera",
                    "CCTV Camera"
                  )
                }
                onMouseEnter={(e) =>
                  handleMouseEnter(e, "CCTV Camera")
                }
                onMouseLeave={handleMouseLeave}
              >
                <Video />
                {!collapsed && <span>CCTV Camera</span>}
              </li>
            )} */}

            {/* ════════════════════════════════════════════════════ */}
            {/* MODULE 18: GREETINGS */}
            {/* ════════════════════════════════════════════════════ */}

            {canView("payment") && (
              <li
                className={`menu-item ${
                  activeMainMenu === "promotions" ? "menu-active" : ""
                }`}
                onClick={() =>
                  handleMainClick(
                    "promotions",
                    false,
                    "/greetings",
                    "Greetings",
                  )
                }
                onMouseEnter={(e) => handleMouseEnter(e, "Greetings")}
                onMouseLeave={handleMouseLeave}
              >
                <Megaphone />

                {!collapsed && <span>Greetings</span>}
              </li>
            )}

            {/* ════════════════════════════════════════════════════ */}
            {/* MODULE 19: REPORTS */}
            {/* ════════════════════════════════════════════════════ */}

            {(canView("reportsSales") ||
              canView("reportsPurchase") ||
              canView("reportsStock") ||
              canView("reportsProducts") ||
              canView("reportsProject") ||
              canView("reportsLead") ||
              canView("reportsDealer") ||
              canView("reportsEmployee") ||
              canView("reportsAttendance") ||
              canView("reportsCarpenter") ||
              canView("reportsExpense") ||
              canView("reportsDamage") ||
              canView("reportsShipment") ||
              canView("reportsSalary") ||
              canView("reportsClients") ||
              canView("reportsTasks") ||
              canView("reportsPurchaseReturn") ||
              canView("reportsSaleReturn") ||
              canView("reportsLeave") ||
              canView("reportsAllowance") ||
              canView("reportsEmployeeAdvance") ||
              canView("reportsJobAssigned") ||
              canView("reportsWorkCompletion") ||
              canView("reportsVehicles") ||
              canView("reportsDrivers") ||
              canView("reportsDriverSalary") ||
              canView("reportsAssets") ||
              canView("reportsBalanceSheet") ||
              canView("reportsSalesLedger")) && (
              <>
                <li
                  className={`menu-item ${
                    activeMainMenu === "reports" ? "menu-active" : ""
                  }`}
                  onClick={() => handleMainClick("reports", true)}
                  onMouseEnter={(e) => {
                    handleMouseEnter(e, "Reports");
                    handleSubmenuParentHover(e, "reports");
                  }}
                  onMouseLeave={() => {
                    handleMouseLeave();
                    handleSubmenuParentLeave();
                  }}
                >
                  <BarChart3 />

                  {!collapsed && (
                    <>
                      <span>Reports</span>
                      <ChevronDown
                        className={`dropdown-arrow ${
                          isOpen("reports") ? "open" : ""
                        }`}
                      />
                    </>
                  )}
                </li>

                {isOpen("reports") && !collapsed && (
                  <ul className="submenu">
                    {(canView("reportsSales") ||
                      canView("reportsPurchase") ||
                      canView("reportsStock") ||
                      canView("reportsProducts") ||
                      canView("reportsProject") ||
                      canView("reportsLead") ||
                      canView("reportsDealer") ||
                      canView("reportsEmployee") ||
                      canView("reportsAttendance") ||
                      canView("reportsCarpenter") ||
                      canView("reportsExpense") ||
                      canView("reportsDamage") ||
                      canView("reportsShipment") ||
                      canView("reportsSalary") ||
                      canView("reportsClients") ||
                      canView("reportsTasks") ||
                      canView("reportsPurchaseReturn") ||
                      canView("reportsSaleReturn") ||
                      canView("reportsLeave") ||
                      canView("reportsAllowance") ||
                      canView("reportsEmployeeAdvance") ||
                      canView("reportsJobAssigned") ||
                      canView("reportsWorkCompletion") ||
                      canView("reportsVehicles") ||
                      canView("reportsDrivers") ||
                      canView("reportsDriverSalary") ||
                      canView("reportsAssets") ||
                      canView("reportsBalanceSheet") ||
                      canView("reportsSalesLedger")) && (
                      <li
                        className={
                          activeSubMenu === "reports-overview"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goToReport(
                            "reports-overview",
                            "Overview",
                            "Reports Overview",
                          )
                        }
                      >
                        <ReportsOverviewIcon className="submenu-icon" />
                        <span>Overview</span>
                      </li>
                    )}

                    {canView("reportsSales") && (
                      <li
                        className={
                          activeSubMenu === "reports-sales"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goToReport("reports-sales", "Sales", "Sales Report")
                        }
                      >
                        <TrendingUp className="submenu-icon" />
                        <span>Sales</span>
                      </li>
                    )}

                    {canView("reportsPurchase") && (
                      <li
                        className={
                          activeSubMenu === "reports-purchase"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goToReport(
                            "reports-purchase",
                            "Purchase",
                            "Purchase Report",
                          )
                        }
                      >
                        <PurchaseIcon className="submenu-icon" />
                        <span>Purchase</span>
                      </li>
                    )}

                    {canView("reportsStock") && (
                      <li
                        className={
                          activeSubMenu === "reports-stock"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goToReport("reports-stock", "Stock", "Stock Report")
                        }
                      >
                        <StockIcon className="submenu-icon" />
                        <span>Stock</span>
                      </li>
                    )}

                    {canView("reportsProducts") && (
                      <li
                        className={
                          activeSubMenu === "reports-products"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goToReport(
                            "reports-products",
                            "Products",
                            "Products Report",
                          )
                        }
                      >
                        <Boxes className="submenu-icon" />
                        <span>Products</span>
                      </li>
                    )}

                    {canView("reportsProject") && (
                      <li
                        className={
                          activeSubMenu === "reports-project"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goToReport(
                            "reports-project",
                            "Project",
                            "Project Report",
                          )
                        }
                      >
                        <ProjectIcon className="submenu-icon" />
                        <span>Project</span>
                      </li>
                    )}

                    {canView("reportsLead") && (
                      <li
                        className={
                          activeSubMenu === "reports-lead"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goToReport("reports-lead", "Lead", "Lead Report")
                        }
                      >
                        <LeadIcon className="submenu-icon" />
                        <span>Lead</span>
                      </li>
                    )}

                    {canView("reportsDealer") && (
                      <li
                        className={
                          activeSubMenu === "reports-dealer"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goToReport(
                            "reports-dealer",
                            "Dealer",
                            "Dealer Report",
                          )
                        }
                      >
                        <DealerIcon className="submenu-icon" />
                        <span>Dealer</span>
                      </li>
                    )}

                    {canView("reportsEmployee") && (
                      <li
                        className={
                          activeSubMenu === "reports-employee"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goToReport(
                            "reports-employee",
                            "Employee",
                            "Employee Report",
                          )
                        }
                      >
                        <EmployeeIcon className="submenu-icon" />
                        <span>Employee</span>
                      </li>
                    )}

                    {canView("reportsAttendance") && (
                      <li
                        className={
                          activeSubMenu === "reports-attendance"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goToReport(
                            "reports-attendance",
                            "Attendance",
                            "Employee Attendance Report",
                          )
                        }
                      >
                        <AttendanceReportIcon className="submenu-icon" />
                        <span>Attendance</span>
                      </li>
                    )}

                    {canView("reportsCarpenter") && (
                      <li
                        className={
                          activeSubMenu === "reports-carpenter"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goToReport(
                            "reports-carpenter",
                            "Carpenter",
                            "Carpenter Report",
                          )
                        }
                      >
                        <CarpenterReportIcon className="submenu-icon" />
                        <span>Carpenter</span>
                      </li>
                    )}

                    {canView("reportsExpense") && (
                      <li
                        className={
                          activeSubMenu === "reports-expense"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goToReport(
                            "reports-expense",
                            "Expense",
                            "Expense Report",
                          )
                        }
                      >
                        <ExpenseIcon className="submenu-icon" />
                        <span>Expense</span>
                      </li>
                    )}

                    {canView("reportsDamage") && (
                      <li
                        className={
                          activeSubMenu === "reports-damage"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goToReport(
                            "reports-damage",
                            "Damage",
                            "Damage Report",
                          )
                        }
                      >
                        <DamageIcon className="submenu-icon" />
                        <span>Damage</span>
                      </li>
                    )}

                    {canView("reportsShipment") && (
                      <li
                        className={
                          activeSubMenu === "reports-shipment"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goToReport(
                            "reports-shipment",
                            "Shipment",
                            "Shipment Report",
                          )
                        }
                      >
                        <ShipmentIcon className="submenu-icon" />
                        <span>Shipment</span>
                      </li>
                    )}

                    {canView("reportsSalary") && (
                      <li
                        className={
                          activeSubMenu === "reports-salary"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goToReport(
                            "reports-salary",
                            "Salary",
                            "Salary Report",
                          )
                        }
                      >
                        <SalaryIcon className="submenu-icon" />
                        <span>Salary</span>
                      </li>
                    )}

                    {canView("reportsClients") && (
                      <li
                        className={
                          activeSubMenu === "reports-clients"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goToReport(
                            "reports-clients",
                            "Clients",
                            "Clients Report",
                          )
                        }
                      >
                        <Users2 className="submenu-icon" />
                        <span>Clients</span>
                      </li>
                    )}

                    {canView("reportsTasks") && (
                      <li
                        className={
                          activeSubMenu === "reports-tasks"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goToReport("reports-tasks", "Tasks", "Task Report")
                        }
                      >
                        <ListTodo className="submenu-icon" />
                        <span>Tasks</span>
                      </li>
                    )}

                    {canView("reportsPurchaseReturn") && (
                      <li
                        className={
                          activeSubMenu === "reports-purchase-return"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goToReport(
                            "reports-purchase-return",
                            "Purchase Return",
                            "Purchase Return Report",
                          )
                        }
                      >
                        <RotateCcw className="submenu-icon" />
                        <span>Purchase Return</span>
                      </li>
                    )}

                    {canView("reportsSaleReturn") && (
                      <li
                        className={
                          activeSubMenu === "reports-sale-return"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goToReport(
                            "reports-sale-return",
                            "Sale Return",
                            "Sale Return Report",
                          )
                        }
                      >
                        <RotateCcw className="submenu-icon" />
                        <span>Sale Return</span>
                      </li>
                    )}

                    {canView("reportsLeave") && (
                      <li
                        className={
                          activeSubMenu === "reports-leave"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goToReport("reports-leave", "Leave", "Leave Report")
                        }
                      >
                        <Leaf className="submenu-icon" />
                        <span>Leave</span>
                      </li>
                    )}

                    {canView("reportsAllowance") && (
                      <li
                        className={
                          activeSubMenu === "reports-allowance"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goToReport(
                            "reports-allowance",
                            "Allowance",
                            "Allowance Report",
                          )
                        }
                      >
                        <Wallet className="submenu-icon" />
                        <span>Allowance</span>
                      </li>
                    )}

                    {canView("reportsEmployeeAdvance") && (
                      <li
                        className={
                          activeSubMenu === "reports-employee-advance"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goToReport(
                            "reports-employee-advance",
                            "Employee Advance",
                            "Employee Advance Report",
                          )
                        }
                      >
                        <CreditCard className="submenu-icon" />
                        <span>Employee Advance</span>
                      </li>
                    )}

                    {canView("reportsJobAssigned") && (
                      <li
                        className={
                          activeSubMenu === "reports-job-assigned"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goToReport(
                            "reports-job-assigned",
                            "Job Assigned",
                            "Job Assigned Report",
                          )
                        }
                      >
                        <Wrench className="submenu-icon" />
                        <span>Job Assigned</span>
                      </li>
                    )}

                    {canView("reportsWorkCompletion") && (
                      <li
                        className={
                          activeSubMenu === "reports-work-completion"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goToReport(
                            "reports-work-completion",
                            "Work Completion",
                            "Work Completion Report",
                          )
                        }
                      >
                        <CheckCircle className="submenu-icon" />
                        <span>Work Completion</span>
                      </li>
                    )}

                    {canView("reportsVehicles") && (
                      <li
                        className={
                          activeSubMenu === "reports-vehicles"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goToReport(
                            "reports-vehicles",
                            "Vehicles",
                            "Vehicle Report",
                          )
                        }
                      >
                        <TruckIcon className="submenu-icon" />
                        <span>Vehicles</span>
                      </li>
                    )}

                    {canView("reportsDrivers") && (
                      <li
                        className={
                          activeSubMenu === "reports-drivers"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goToReport(
                            "reports-drivers",
                            "Drivers",
                            "Driver Report",
                          )
                        }
                      >
                        <UserCircle className="submenu-icon" />
                        <span>Drivers</span>
                      </li>
                    )}

                    {canView("reportsDriverSalary") && (
                      <li
                        className={
                          activeSubMenu === "reports-driver-salary"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goToReport(
                            "reports-driver-salary",
                            "Driver Salary",
                            "Driver Salary Report",
                          )
                        }
                      >
                        <Wallet className="submenu-icon" />
                        <span>Driver Salary</span>
                      </li>
                    )}

                    {canView("reportsAssets") && (
                      <li
                        className={
                          activeSubMenu === "reports-assets"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goToReport(
                            "reports-assets",
                            "Assets",
                            "Assets Report",
                          )
                        }
                      >
                        <Landmark className="submenu-icon" />
                        <span>Assets</span>
                      </li>
                    )}

                    {canView("reportsBalanceSheet") && (
                      <li
                        className={
                          activeSubMenu === "reports-balance-sheet"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goToReport(
                            "reports-balance-sheet",
                            "Balance Sheet",
                            "Balance Sheet Report",
                          )
                        }
                      >
                        <BalanceSheetIcon className="submenu-icon" />
                        <span>Balance Sheet</span>
                      </li>
                    )}

                    {canView("reportsSalesLedger") && (
                      <li
                        className={
                          activeSubMenu === "reports-sales-ledger"
                            ? "submenu-active"
                            : ""
                        }
                        onClick={() =>
                          goToReport(
                            "reports-sales-ledger",
                            "Sales Ledger",
                            "Sales Ledger Report",
                          )
                        }
                      >
                        <SalesLedgerIcon className="submenu-icon" />
                        <span>Sales Ledger</span>
                      </li>
                    )}
                  </ul>
                )}
              </>
            )}

            {/* ════════════════════════════════════════════════════ */}
            {/* MODULE 20: TALLY SYNC */}
            {/* ════════════════════════════════════════════════════ */}

            {canView("tallySync") && (
              <li
                className={`menu-item ${
                  activeMainMenu === "tally-sync" ? "menu-active" : ""
                }`}
                onClick={() =>
                  handleMainClick(
                    "tally-sync",
                    false,
                    "/tally-sync",
                    "Tally Sync",
                  )
                }
                onMouseEnter={(e) => handleMouseEnter(e, "Tally Sync")}
                onMouseLeave={handleMouseLeave}
              >
                <Activity size={18} />

                {!collapsed && <span>Tally Sync</span>}
              </li>
            )}
          </ul>
        </div>
      </aside>

      {/* ─────────────────────────────────────────
          COLLAPSED SIDEBAR TOOLTIP
      ───────────────────────────────────────── */}

      {hoveredTooltip && collapsed && !hoveredSubmenuParent && (
        <div
          className="sidebar-tooltip"
          style={{
            position: "fixed",
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
            transform: "translateY(-50%)",
            zIndex: 999999,
          }}
        >
          {hoveredTooltip}
        </div>
      )}

      {/* ─────────────────────────────────────────
          COLLAPSED SUBMENU TOOLTIP
      ───────────────────────────────────────── */}

      {hoveredSubmenuParent &&
        collapsed &&
        submenuData[hoveredSubmenuParent] && (
          <div
            className="submenu-tooltip-container"
            style={{
              position: "fixed",
              top: `${submenuTooltipPosition.top}px`,
              left: `${submenuTooltipPosition.left}px`,
              zIndex: 999999,
            }}
            onMouseEnter={handleSubmenuTooltipEnter}
            onMouseLeave={handleSubmenuTooltipLeave}
          >
            {submenuData[hoveredSubmenuParent]
              .filter((item) => item.canView)
              .map((item) => {
                const IconComponent = item.icon;

                return (
                  <div
                    key={item.key}
                    className={`submenu-tooltip-item ${
                      activeSubMenu === item.key ? "submenu-tooltip-active" : ""
                    }`}
                    onClick={() => {
                      setActiveSubMenu(item.key);
                      setActiveMainMenu(hoveredSubmenuParent);
                      navigate(item.path);
                      setTitle(item.label);
                      setHoveredSubmenuParent(null);
                    }}
                  >
                    {isReactIcon(IconComponent) ? (
                      <RIcon
                        icon={IconComponent}
                        size={18}
                        className="submenu-tooltip-icon"
                      />
                    ) : (
                      <IconComponent
                        size={18}
                        className="submenu-tooltip-icon"
                      />
                    )}

                    <span>{item.label}</span>
                  </div>
                );
              })}
          </div>
        )}
    </>
  );
};

export default Sidebar;
